import { Link } from "react-router-dom";

import { useAuth } from "@/contexts/auth-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import type { CalibrationStatusData } from "@/hooks/queries/calibration-status-types";
import { useCalibrationStatus } from "@/hooks/queries/useCalibrationStatus";
import {
  DEADLINE_REVIEW_CTA_LABEL,
  DEADLINE_REVIEW_MAILTO,
  DEADLINE_STATE_BADGE_VARIANT,
  DEADLINE_STATE_LABEL,
  deadlineDaysLabel,
} from "@/lib/calibration-deadline";
import { formatDate } from "@/lib/format-date";
import {
  LEGACY_VERIFICATION_PENDING_REASON,
  ORG_LAPSE_CONTACT_ADMIN,
  isOrgSeatLapsed,
  selectLegacyOnboardingState,
  selectSubscriptionPillState,
} from "@/lib/subscription-copy";
import {
  isResolvedSubscription,
  useSubscriptionState,
  type SubscriptionState,
} from "@/hooks/queries/useSubscriptionState";
import { CLINICIAN_ROLES } from "@/routes/route-access";

// Story 10.9 — Screen 14 "Access & readiness" card: data-driven copy, one disabled CTA.

export type ReadinessState =
  | "gated"
  | "gated-unknown"
  | "awaiting-payment"
  | "awaiting-activation"
  | "pre-calibration"
  | "active"
  | "lapsed"
  | "neutral";

/**
 * Precedence: gated → pre-calibration → active → lapsed → neutral (AC4), with
 * Story 16.7 splitting the FIRST branch in two rather than reordering any of it,
 * and the PR #741 review splitting it twice more — into four.
 *
 * `gated` used to answer for every legacy clinician whose account is not yet
 * enabled, and its copy says the subscription is unpaid. Since 16.7 that
 * sentence has a population it is false for: a clinician who HAS paid and is
 * waiting on the activation event.
 *
 * The 16.7 split was BINARY — "activating" or `gated` — and that left the middle
 * state on the wrong copy. A STAGE-2 clinician (CITS has already approved the
 * certificate) who has not yet paid was told access is "locked until CITS
 * verifies your certificate", with a disabled-CTA reason saying the same, while
 * `SubscriptionSection` forty pixels below correctly said "Payment required" /
 * "Complete payment to access calibration.". They chase CITS about an approved
 * certificate instead of paying. `awaiting-payment` is that third state, and it
 * names PAYMENT as the blocker on both the body and the CTA reason.
 *
 * `gated-unknown` is the fourth, and it exists because the same false sentence
 * survived the split for the FIRST PAINT: a stage-2 clinician's read has not
 * resolved yet when the card mounts, and an unresolved read fell through to
 * `gated` too. So the certificate copy showed for the length of the
 * `/billing/subscription-state` round trip and only then flipped. It answers the
 * neutral copy instead — while keeping the disabled CTA, because "we do not know
 * the billing state" never means "this gated account may enter calibration".
 *
 * DW-458 (16.7 review, HIGH) — this function used to make that split itself,
 * with `isPreCalibrationSubscription` alone, while its sibling in
 * `SubscriptionSection` made the same split WITH a `certVerified` term. Two
 * renderers, one question, two answers, on the same page. Both now call
 * `selectLegacyOnboardingState` (`lib/subscription-copy.ts`) and the billing
 * states come from `selectSubscriptionPillState` beside it, so this card cannot
 * drift from the pill again.
 */
export function selectReadinessState(input: {
  isLegacyGated: boolean;
  certVerified: boolean;
  subscription: SubscriptionState;
}): ReadinessState {
  // Gated FIRST, before the billing read: `/billing/*` 403'd for gated users
  // before Story 16.7's allow-list widening, and an UNRESOLVED read still has to
  // land somewhere, so keying the whole branch off the subscription would wedge
  // this card (the 10.8 lesson). The branch honours that by answering for BOTH
  // outcomes of an unresolved read itself — `gated` at stage 1, `gated-unknown`
  // at stage 2 — rather than letting either fall out to the billing states.
  //
  // The `certVerified` term the shared selector carries is what makes those two
  // answers different: a stage-1 clinician (certificate still under review) can
  // reach a resolved billing read too, and must never be told "your payment has
  // been received" — FR39 puts payment AFTER approval, so a stage-1 paid marker
  // cannot legitimately exist. Stage 1 is Screen 14b's surface and keeps the 14b
  // `gated` copy.
  if (input.isLegacyGated) {
    const onboarding = selectLegacyOnboardingState({
      isLegacyGated: true,
      certVerified: input.certVerified,
      subscription: input.subscription,
    });
    if (onboarding === "activating") return "awaiting-activation";
    if (onboarding === "awaiting_payment") return "awaiting-payment";
    // STAGE 1 — the certificate is still under review, so BOTH blockers are
    // genuinely outstanding and `gated`'s sentence, which names both, is true
    // without consulting the billing read at all. That independence is the
    // point: `/billing/*` 403'd for gated users before Story 16.7's allow-list
    // widening, and this card must never wedge on a read it may not get (the
    // 10.8 lesson). 14b is this state's validated screen.
    if (!input.certVerified) return "gated";
    // STAGE 2 with the billing read still UNRESOLVED (in flight, 403, errored,
    // malformed). `gated` used to answer here too, and it was a false claim for
    // the length of the round trip: this clinician's certificate IS approved, so
    // the sentence and its CTA reason blamed CITS and sent them chasing an
    // approval they already have, until the read landed and flipped the card to
    // the payment copy. The `neutral` guard below exists for exactly this — never
    // a claim on an unknown state — but sits after this branch and can never be
    // reached from it, so the branch answers for itself (PR #741 review).
    if (!isResolvedSubscription(input.subscription)) return "gated-unknown";
    // Resolved, cert verified, and still no onboarding answer: a claim the
    // in-product checkout does not own (org seat / manual override), or the
    // never-produced active one. `gated` remains the conservative answer.
    return "gated";
  }
  // Unresolved (loading/403) renders neutral — never a false "active". Kept as
  // its own guard rather than folded into the mapper's `null`: `isResolvedSubscription`
  // additionally refuses a resolved-but-malformed payload (no boolean `isActive`),
  // which must render neutral here.
  if (!isResolvedSubscription(input.subscription)) return "neutral";
  // The billing states, from the shared mapper. `pending_calibration` is the
  // `notes === "Pre-calibration"` marker — the only "not yet calibrated" signal
  // (no Epic 8 BE yet) — and it requires `status === "lapsed"`, which narrows
  // this branch: an ACTIVE state carrying the marker reports "active" rather
  // than "pre-calibration". That combination is reachable by DATA, not by
  // provider code: both subscription providers hardcode `notes=None` on their
  // active state (`individual_subscription_provider.py:119`,
  // `org_seat_provider.py:173`), but the override path returns operator free
  // text verbatim (`manual_override_provider.py:101`;
  // `create_entitlement_override/schemas.py` bounds `notes` by length alone, no
  // enum), so a super_admin who types "Pre-calibration" produces
  // `{isActive: true, notes: "Pre-calibration"}`. The narrowing is the fix, not
  // the risk — that clinician used to get the pre-calibration copy
  // ("calibration is not yet complete", scoring locked), false for a live
  // entitlement, and now gets the active copy.
  const pillState = selectSubscriptionPillState({
    subscription: input.subscription,
  });
  if (pillState === "pending_calibration") return "pre-calibration";
  return pillState === "active" ? "active" : "lapsed";
}

// 14b body copy (shell chrome/banner are AppShell's; this card carries the reason).
const GATED_COPY =
  "Your account is currently disabled. Calibration, scoring, and the clinician " +
  "workspace are locked until CITS verifies your certificate and your annual " +
  "subscription is paid.";

// PR #741 review — the CERT-VERIFIED, UNPAID half of the old `gated` branch.
// Same provenance caveat as the constant below: no mockup renders this moment, so
// the wording is pinned here and flagged for PM ratification. It must not name the
// certificate as a blocker (CITS has approved it) and it must point at the one
// action that remains, which lives in the Subscription section on this same
// screen — the "See the Subscription section below…" pointer is the LAPSED_COPY
// pattern already in this file, and "annual subscription is paid" is GATED_COPY's
// own phrase kept verbatim.
const AWAITING_PAYMENT_COPY =
  "Your certificate is verified. Calibration, scoring, and the clinician " +
  "workspace stay locked until your annual subscription is paid. See the " +
  "Subscription section below to complete payment.";

// Story 16.7 (AC13) — the paid half of the old `gated` branch. NOT client-
// validated: no mockup renders this moment (Screen 14 shows the post-activation
// active state; 14b shows the pre-verification one), so the wording is pinned
// here and flagged for PM ratification. It must not claim the subscription is
// unpaid, and it must not promise calibration is open yet — the account is
// enabled by an event that has not arrived.
const AWAITING_ACTIVATION_COPY =
  "Your payment has been received and your account is being enabled. " +
  "Calibration unlocks as soon as that finishes.";

const PRE_CALIBRATION_COPY =
  "Your calibration is not yet complete. Live AMPS scoring workflows stay " +
  "locked until it completes.";

// Screen 14 copy, verbatim.
const ACTIVE_COPY =
  "Your calibration profile is active for this account and live AMPS scoring " +
  "workflows are enabled.";

// Never the active claim from an inactive resolved state; the Subscription
// section below carries the renewal action — B2C only.
const LAPSED_COPY =
  "Live AMPS scoring workflows are currently unavailable while your " +
  "subscription is inactive. See the Subscription section below to renew.";

// Story 13.6 — the org_seat branch: a B2B clinician has NO renew control
// anywhere in the product (renewal is org-admin/CITS scope, Story 13.3), so
// the copy must never point at one (closes the Epic-13 deferred-work item).
const ORG_LAPSED_COPY =
  "Live AMPS scoring workflows are currently unavailable while your " +
  `organization's contract is inactive. ${ORG_LAPSE_CONTACT_ADMIN}`;

const NEUTRAL_COPY = "Checking your access state…";

const BODY_COPY: Record<ReadinessState, string> = {
  gated: GATED_COPY,
  // The same sentence `neutral` uses, and deliberately not a fifth wording: the
  // reader's situation IS "we are still checking", and the only thing that makes
  // `gated-unknown` a separate state is the CTA treatment below.
  "gated-unknown": NEUTRAL_COPY,
  "awaiting-payment": AWAITING_PAYMENT_COPY,
  "awaiting-activation": AWAITING_ACTIVATION_COPY,
  "pre-calibration": PRE_CALIBRATION_COPY,
  active: ACTIVE_COPY,
  lapsed: LAPSED_COPY,
  neutral: NEUTRAL_COPY,
};

// 14b tooltip for the gated legacy clinician — no calibration access until CITS
// verifies the certificate, so the CTA stays disabled with this reason. The
// sentence itself lives in `lib/subscription-copy.ts`: 14b puts the identical
// words on the Subscription card's disabled "Pay annual subscription" one
// section below, and a reworded copy must not reach one button and miss the
// other (`ORG_LAPSE_CONTACT_ADMIN`'s precedent).
const GATED_CTA_REASON = LEGACY_VERIFICATION_PENDING_REASON;

// PR #741 review — the same disabled CTA for the cert-verified, unpaid clinician.
// `GATED_CTA_REASON` was flatly wrong for them: their certificate IS verified, so
// the sentence sent them to CITS instead of to the payment control one section
// below. Phrased off `AWAITING_ACTIVATION_CTA_REASON`'s "Available …" shape.
const AWAITING_PAYMENT_CTA_REASON =
  "Available once your annual subscription is paid.";

// Story 16.7 — the same disabled CTA, a different reason. A paid clinician
// whose account is not yet enabled still cannot reach `/calibration/*` (the API
// answers 403 LEGACY-002 until the flip), so the button stays disabled; naming
// the certificate as the blocker would be false, since theirs is already
// verified.
const AWAITING_ACTIVATION_CTA_REASON =
  "Available as soon as your account is enabled.";

/**
 * The four states whose CTA is a DISABLED button rather than a live link — a
 * legacy clinician's account is not enabled in any of them, and `/calibration/*`
 * refuses them at the API (403 LEGACY-002). Keeping them in one set is what
 * stops the split from accidentally handing one of the post-16.4 states a live
 * link into a route that would bounce it; only the REASON differs between them.
 *
 * `gated-unknown` is in the set for precisely that reason. It reads as an
 * unknown BILLING state, but the account being disabled is a JWT fact, settled
 * without any billing read at all — so the link must stay shut even while the
 * copy above it says nothing. It borrows the activation sentence because that is
 * the only one of the three reasons naming no billing fact: on an unresolved
 * read we do not know whether payment is outstanding, and the other two would
 * each assert an answer to it.
 */
const CTA_DISABLED_REASON: Partial<Record<ReadinessState, string>> = {
  gated: GATED_CTA_REASON,
  "gated-unknown": AWAITING_ACTIVATION_CTA_REASON,
  "awaiting-payment": AWAITING_PAYMENT_CTA_REASON,
  "awaiting-activation": AWAITING_ACTIVATION_CTA_REASON,
};

/**
 * The ONE reason element behind the disabled CTA (DW-673). Exactly four states
 * disable the button and only the sentence differs, so there is one element and
 * one `aria-describedby` reference rather than a per-state id.
 *
 * It replaced a native `title`, which the standing gh-555/gh-568 rule forbids on
 * an `aria-disabled` control: the iPad PWA is the primary clinical surface and
 * `title` is mouse-only there, so the reason a clinician's only route into
 * calibration is shut was unreachable on the device most of them use. The shape
 * is the house one — `EditMemberDialog.tsx:73-98` for the id, and this run's own
 * `CalibrationSubNav` / `CalibrationCasesScreen` for the visible sentence.
 */
const CTA_DISABLED_REASON_ID = "access-readiness-cta-disabled-reason";

/**
 * Story 20.1 (FR86, AC12) — the deadline line, ADDITIVE to the eight-state
 * readiness ladder above. It is not a ninth `ReadinessState`: the ladder answers
 * "can this clinician score?" and this answers "how long do they have left to
 * calibrate?", the two move independently, and folding the deadline into the
 * ladder's precedence chain would make one of them shadow the other.
 *
 * Rendered for `active` and `due_soon` only. `not_applicable` is the exemption
 * (AC6). `held` is deliberately absent too: AC12 names two states, and its own
 * note assigns Screen 38 panel 4a — the hold copy, the field grid, the paused
 * CTA — to Story 20.2. A minimal "On hold" line authored here would be replaced
 * one story later and would meanwhile sit beside a CTA that is still live, which
 * is the one combination panel 4a does not draw. The Calibration Overview is
 * where a held clinician reads their dates today.
 *
 * The label and the badge variant come from `lib/calibration-deadline.ts`, which
 * the Overview reads too — Screen 38 panel 4a: "No surface re-derives its own
 * wording." The `active` / `due_soon` sentences below have NO mockup (4a draws
 * only `held`), so they are authored here and flagged for design review.
 */
function CalibrationDeadlineLine({
  data,
  dueSoon,
}: {
  data: CalibrationStatusData;
  dueSoon: boolean;
}) {
  const state = dueSoon ? "due_soon" : "active";
  const deadline = formatDate(data.calibration_deadline_at);
  const days = data.days_remaining;

  return (
    <div
      role="group"
      aria-label="Calibration deadline"
      className={
        // One step of emphasis for `due_soon`, on the muted-warning pair only
        // (AC11) — the same triple the `pending` badge beside it carries. Never
        // an error token: a deadline two weeks out is a reminder, and nothing on
        // this card is disabled by it.
        dueSoon
          ? "flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--bg-warning-secondary)] bg-[color:var(--bg-warning-primary)] px-4 py-3"
          : "flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--border-primary)] px-4 py-3"
      }
    >
      <Badge variant={DEADLINE_STATE_BADGE_VARIANT[state]}>
        {DEADLINE_STATE_LABEL[state]}
      </Badge>
      <p
        className={
          dueSoon
            ? "text-sm text-[color:var(--text-warning)]"
            : "text-sm text-[color:var(--text-secondary)]"
        }
      >
        Complete your calibration by{" "}
        <span className="tabular-nums">{deadline}</span>.
        {days != null ? (
          <>
            {" "}
            <span className="tabular-nums">
              {days === 0 ? "Today is the last day." : `${deadlineDaysLabel(days)} remaining.`}
            </span>
          </>
        ) : null}
        {dueSoon
          ? " Ask CITS for a deadline review before that date if you need more time."
          : null}
      </p>
    </div>
  );
}

const TOTAL_CALIBRATION_CASES = 5;

/**
 * Story 20.2 (FR86, AC11) — Screen 38 panel 4a, the hold block 20.1 deferred.
 *
 * The comment above `CalibrationDeadlineLine` explains why this is a SECOND
 * block rather than a third branch of that one: `active` and `due_soon` render
 * a single reminder line, and `held` renders a pill, a cause paragraph and a
 * four-field read-only grid. Folding them together would produce one component
 * with two disjoint bodies and a boolean deciding which.
 *
 * Still ADDITIVE to the eight-state `ReadinessState` ladder, which this story
 * does not touch (AC11): `held` is not a ninth state. The ladder answers "can
 * this clinician score?" and this answers "why has their calibration stopped?".
 *
 * Every word keyed on `deadline_state === "held"` and nothing else (AC12) — no
 * date comparison and no day arithmetic on this surface. The label and badge
 * come from `lib/calibration-deadline.ts`, which the Overview reads too.
 *
 * W3 deviation, recorded rather than followed: panel 4a draws "Open calibration
 * overview" DISABLED with a native `title`. Both halves are declined. The
 * `title` is D9's standing rule (mouse-only on the iPad PWA). Disabling the link
 * is declined because the Overview is where the four guarantees, the deadline
 * dates and "What to do next" actually live — a held clinician sent to Profile
 * would otherwise find the explanation of the hold behind a control the hold
 * disabled, which is the dead end panel 3's own caption warns against. AC11
 * lists what this block renders and that button is not on the list.
 */
function CalibrationHeldBlock({ data }: { data: CalibrationStatusData }) {
  const deadline = formatDate(data.calibration_deadline_at);
  const completed = Math.max(
    0,
    Math.min(TOTAL_CALIBRATION_CASES, data.completed_cases),
  );
  const fields: { label: string; value: string }[] = [
    { label: "Calibration deadline", value: deadline },
    {
      label: "Deadline status",
      value:
        data.held_since != null
          ? `On hold since ${formatDate(data.held_since)}`
          : "On hold",
    },
    {
      label: "Cases completed",
      value: `${completed} / ${TOTAL_CALIBRATION_CASES} — saved and retained`,
    },
    { label: "Live AMPS scoring", value: "Not available while on hold" },
  ];

  return (
    <div
      role="group"
      aria-label="Calibration deadline"
      className="flex flex-col gap-3 rounded-xl border border-[color:var(--border-primary)] px-4 py-3"
    >
      <Badge variant={DEADLINE_STATE_BADGE_VARIANT.held}>
        {DEADLINE_STATE_LABEL.held}
      </Badge>
      <p className="text-sm text-[color:var(--text-secondary)]">
        Your calibration is on <strong>temporary hold</strong> because the
        deadline passed on <span className="tabular-nums">{deadline}</span>. Your account and
        all existing information are retained and your purchase is not forfeited —
        you are simply not eligible for live AMPS scoring until a CITS super admin
        extends the deadline or reactivates the account.
      </p>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((field) => (
          <div key={field.label}>
            <dt className="text-xs text-[color:var(--text-tertiary)]">
              {field.label}
            </dt>
            <dd className="tabular-nums text-sm text-[color:var(--text-primary)]">
              {field.value}
            </dd>
          </div>
        ))}
      </dl>
      <div>
        <Button asChild variant="secondary">
          <a href={DEADLINE_REVIEW_MAILTO}>{DEADLINE_REVIEW_CTA_LABEL}</a>
        </Button>
      </div>
    </div>
  );
}

export function AccessReadinessCard() {
  const { user, isLegacyGated, hasRole } = useAuth();
  // `keepResolvedOnError: true` — the SAME posture `SubscriptionSection` takes
  // (gh-622 PR review). The two cards render on one screen off ONE cache entry
  // (`useSubscriptionState.ts` hoists the shared query options), but the retain
  // decision is per-OBSERVER: an errored query that still holds a resolved payload
  // returns `{status:"loading"}` without the opt-in. This card lacked it, so a
  // failed refetch collapsed it to an unresolved read while its sibling kept
  // rendering the resolved one — and for a legacy clinician `null` from the shared
  // selector lands on `gated`, i.e. "locked until CITS verifies your certificate"
  // beside "Payment required". Story 16.7's 15s poll on that sibling makes a blip
  // routine for exactly this cohort, so the two cards would contradict each other
  // about whether the money arrived.
  //
  // The hook's docstring reserves the default error→loading posture for consumers
  // where "a transient failure never disables writes". That reason does not reach
  // this card: it renders COPY and a navigation CTA, never a write gate — the
  // write gates key on `isSubscriptionLapsed`, which this card does not call.
  const subscription = useSubscriptionState({ keepResolvedOnError: true });

  // Story 20.1 (AC12, boundary row 9) — the card reads the calibration status
  // ITSELF rather than taking it as a prop, exactly as it already owns the
  // subscription read above. It is the same hook and the same query key the
  // Calibration Overview and the Story 8.1 gate use, so a clinician who has
  // been through either pays nothing here.
  //
  // Not fetched for the `already_certified` bypass: that claim settles the
  // question without a round trip (the gate short-circuits on it too), and the
  // backend would answer `not_applicable` anyway. A refused or pending read
  // renders no line at all — this card must never wedge on a read it may not
  // get (the 10.8 lesson the subscription comment above records).
  const calibration = useCalibrationStatus({
    enabled: hasRole(...CLINICIAN_ROLES) && user?.alreadyCertified !== true,
  });

  // Story 15.9 (#719) — visibility gate. A role with no clinical entitlement sees
  // NO readiness card: every one of this card's states describes a clinical
  // subscription, and an org_admin's resolves to `lapsed` — a true rendering of
  // the payload and a false statement about the reader. The predicate is a
  // POSITIVE allow-list on ROLE, never on the resolved entitlement (a lapsed B2C
  // clinician resolves the identical `no_entitlement` shape and must keep this
  // card). It also removes the dead end: "Open calibration overview" points at a
  // route `route-access.ts` governs with CLINICIAN_ROLES.
  // The hooks above stay above this return, as React's rules of hooks require.
  if (!hasRole(...CLINICIAN_ROLES)) return null;

  const state = selectReadinessState({
    isLegacyGated,
    certVerified: Boolean(user?.certVerified),
    subscription,
  });
  // Source-branched lapsed copy (Story 13.6 AC6): org_seat → org administrator.
  // `isOrgSeatLapsed` is the shared predicate for that question (PR-584 review),
  // not a local restatement of `status === "lapsed" && source === "org_seat"`.
  const bodyCopy =
    state === "lapsed" && isOrgSeatLapsed(subscription)
      ? ORG_LAPSED_COPY
      : BODY_COPY[state];
  const ctaDisabledReason = CTA_DISABLED_REASON[state];
  const deadline = calibration.data;
  const deadlineState = deadline?.deadline_state;
  const showDeadline = deadlineState === "active" || deadlineState === "due_soon";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access &amp; readiness</CardTitle>
      </CardHeader>
      <div className="mt-4 flex flex-col gap-4">
        <p className="text-sm text-[color:var(--text-secondary)]">{bodyCopy}</p>
        {showDeadline && deadline ? (
          <CalibrationDeadlineLine
            data={deadline}
            dueSoon={deadlineState === "due_soon"}
          />
        ) : null}
        {deadlineState === "held" && deadline ? (
          <CalibrationHeldBlock data={deadline} />
        ) : null}
        <div className="flex flex-col items-start gap-1">
          {ctaDisabledReason ? (
            // seam-109: a legacy clinician whose account is not enabled has NO
            // calibration access yet — keep the disabled button + reason (14b
            // pattern), never a live link into calibration. Story 16.7 widened
            // this from one state to two; the reason sentence is what differs.
            //
            // DW-673: the reason is VISIBLE TEXT linked by `aria-describedby`,
            // never a native `title` (gh-555/gh-568). `items-start` keeps the
            // button at its intrinsic width inside the new column.
            <>
              <Button
                type="button"
                variant="secondary"
                disabled
                aria-disabled="true"
                aria-describedby={CTA_DISABLED_REASON_ID}
              >
                Open calibration overview
              </Button>
              <p
                id={CTA_DISABLED_REASON_ID}
                className="text-sm text-[color:var(--text-tertiary)]"
              >
                {ctaDisabledReason}
              </p>
            </>
          ) : (
            // seam-109 CLOSED (Story 8.7): the /calibration/overview route now exists,
            // so every non-gated state gets a live link (Screen 14).
            <Button asChild variant="secondary">
              <Link to="/calibration/overview">Open calibration overview</Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
